import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { useAsyncValue, useNavigation, useFetcher } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { format } from "date-fns";
// import { Button, Modal, Space } from 'antd';
import {
    AffiliateManagerStats, ClientStatsResponse, CMAddressBook, CountryStatsResponse, DatabaseStatsResponse, GetCampaignDetailsResponse, MessageCostResponse, PricingModelStatsResponse, StatsResponse, TagStatsResponse,
} from "services/apiService";
import axiosInstance from "services/axiosInstance";
import { StatsStatus, STATS_STATUSES } from "constants/constants";
import { Alert } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ToastContainer } from "react-toastify";
import { encode_text_base64 } from "router/actions";
import { setMessage } from "messages/messages";

const MAX_SENDER_TEXT_LENGTH = 11;
const MAX_MESSAGE_TEXT_LENGTH = 160;
const STOP_MESSAGE = "STOP 36047"

interface Currency { id: number; name: string; }
interface Advertiser { id: number; name: string; }
interface Pricing { model: PricingModelStatsResponse | null; price: number | string; }
export enum CampaignFormActions { CREATE, EDIT, CLONE }


function analyzeSmsCharacters(text: string) {
    // Liste des caractères GSM-7 de base
    const gsm7BitChars =
        "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\u000c" +
        "ÆæßÉ !\"#$%&'()*+,-./0123456789:;<=>?" +
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz" +
        "¡¿à";

    // Liste des caractères étendus (chaque caractère prend 2 caractères en GSM-7)
    const gsm7BitExtendedChars = "^{}\\[~]|€";

    const LINK_PLACEHOLDER_LENGTH = 22;

    // Détection des caractères non supportés
    const unsupportedChars: Record<string, number> = {};
    let isUnicode = false;

    for (const char of text) {
        const codePoint = char.codePointAt(0) || 0;
        if (!gsm7BitChars.includes(char) && !gsm7BitExtendedChars.includes(char)) {
            unsupportedChars[char] = (unsupportedChars[char] || 0) + 1;
            isUnicode = true;
        }
    }

    // Détection des liens
    const linkRegex = /(https?:\/\/[^\s]+)/g;
    const detectedLinks = text.match(linkRegex) || [];

    // Remplacement des liens par un espace de 21 caractères
    let textWithoutLink = text.replace(linkRegex, " ".repeat(LINK_PLACEHOLDER_LENGTH)).trim();

    // Déterminer l'encodage
    const encoding = isUnicode ? "UCS-2 (Unicode)" : "GSM 7-bit";
    const maxChars = isUnicode ? 69 : 159; // 159 au lieu de 160 pour être sûre et 69 au lieu de 70

    // Calcul de la nouvelle longueur après remplacement des liens
    // const adjustedTextLength = textWithoutLink.length +detectedLinks.length * (LINK_PLACEHOLDER_LENGTH - detectedLinks.join("").length);

    // Ajustement pour assurer le comptage des nombres de calcul
    let textAdjusted = text.replace(linkRegex, " ".repeat(LINK_PLACEHOLDER_LENGTH));
    const adjustedTextLength = textAdjusted.length;

    // Nombre de messages requis
    const numberOfMessages = Math.ceil(adjustedTextLength / maxChars);
    return {
        encoding,
        maxCharactersPerMessage: maxChars,
        messageLength: adjustedTextLength,
        unsupportedCharacters: unsupportedChars,
        detectedLinks,
        numberOfMessages,
        textWithoutLink,
    };
}


const CampaignForm = ({ action, state }: { action: CampaignFormActions, state: any }) => {
    const navigation = useNavigation();
    const inputDisabled = navigation.state === "submitting";
    console.log('** CampaignForm **')
    console.log('** state : ', state?.previous)
    const fetcher = useFetcher();
    console.log('**\n  fetcher : ', fetcher)

    const theme = useTheme();

    // Get loader data
    const [{ databases, countries, tags, clients, models }, addressBooks, initialValues,] = useAsyncValue() as [StatsResponse, CMAddressBook[], GetCampaignDetailsResponse | undefined,];

    // Sort databases by name alphabetically
    const sortedDatabases = useMemo(() => [...databases].sort((a, b) => a.name.localeCompare(b.name)), [databases]);

    // Kind of databases
    const defaultDatabaseKind = "all";
    const databaseKinds = useMemo(() => { return [defaultDatabaseKind, ...new Set(databases.map((elt) => elt.kind))]; }, [databases]);
    const [databaseKind, setDatabaseKind] = useState(defaultDatabaseKind);

    // Filter database by kind
    const filteredDatabases = useMemo(() => { return sortedDatabases.filter((elt) => databaseKind === "all" || elt.kind === databaseKind); }, [sortedDatabases, databaseKind]);

    // Extract currencies from country list
    const currencies: Currency[] = useMemo(() => {
        let currencies = [
            ...new Set(
                countries.map((country) => {
                    return {
                        id: country.currencyid,
                        name: country.currencyname,
                    };
                })
            ),
        ];
        currencies = [
            ...new Map(
                currencies.map((currency) => [currency.id, currency])
            ).values(),
        ];
        return currencies;
    }, [countries]);


    // ======================================= Get advertisers from stats api =======================================
    const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
    const [advertiserInputValue, setAdvertiserInputValue] = useState<string>((initialValues && initialValues.stats_advertiser_name) || "");
    // Address books with default value
    const defaultAddressBook: CMAddressBook = useMemo(() => ({
        id: "N/A",
        name: "Create new address book with the same name as the campaign",
        contactsAmount: 0
    }), []
    );
    const addressBooksWithDefault = useMemo(() => [defaultAddressBook, ...addressBooks], [defaultAddressBook, addressBooks]);

    // Form values
    const [database, setDatabase] = useState<DatabaseStatsResponse | null>((initialValues && databases.find((d) => d.id === initialValues.stats_base_id)) || null);
    const [country, setCountry] = useState<CountryStatsResponse | null>((initialValues && countries.find((c) => c.id === initialValues.stats_country_id)) || null);
    const [currency, setCurrency] = useState<Currency | null>((initialValues && currencies.find((c) => c.id === initialValues.stats_currency_id)) || null);

    // ================================== advertiser info =================================
    const [advertiser, setAdvertiser] = useState<Advertiser | null>(null);
    const [tag, setTag] = useState<TagStatsResponse | null>((initialValues && tags.find((t) => t.id === initialValues.stats_tags_id)) || null);


    const [client, setClient] = useState<ClientStatsResponse | null>((initialValues && clients.find((c) => c.id === initialValues.stats_clients_id)) || null);
    const [affiliateManager, setAffiliateManager] = useState<AffiliateManagerStats | null>(null);
    const [pricings, setPricings] = useState<Pricing[]>((initialValues && (JSON.parse(initialValues.stats_model) as { modelId: number; payout: number; }[]
    ).map(({ modelId, payout }): Pricing => ({ model: models.find((model) => model.id === modelId) || null, price: payout, })
    )) || [{ model: null, price: "" }]);

    const [segment, setSegment] = useState(initialValues ? initialValues.stats_segment : "");

    const [statsStatus, setStatsStatus] = useState<StatsStatus | null>(
        (initialValues && STATS_STATUSES.find((status) => status.id.toString() === initialValues.stats_status)) || STATS_STATUSES[0]
    );
    const [volume, setVolume] = useState<number | "">((initialValues && initialValues.volume) || "");
    const [scheduledDateTime, setScheduledDateTime] = useState<Date | null>((initialValues && new Date(initialValues.stats_scheduled_date)) || null);
    const [sender, setSender] = useState((initialValues && initialValues.cm_sender) || "");


    // ================================ GENERATE ADDRESSBOOK FOR CLONE AND EDIT ================================
    // const [addressBook, setAddressBook] = useState<CMAddressBook | null>(
    //     ((initialValues && (state?.previous === 'clone') && (state?.previous === 'edit')) &&
    //         addressBooks.find((ab) => ab.id === initialValues.cm_group_id)) || defaultAddressBook
    // );

    const [addressBook, setAddressBook] = useState<CMAddressBook | null>(
        (state?.previous === 'edit' && initialValues && initialValues.cm_group_id) ?
            addressBooks.find((ab) => ab.id === initialValues.cm_group_id) || null :
            defaultAddressBook
    );

    console.log("ADD BOOK INIT VALUE :", addressBook);

    const campaignName = useMemo(() => {
        const nameParts: string[] = [];
        if (advertiser) nameParts.push(advertiser.name);
        if (client) nameParts.push(client.name);
        if (database) nameParts.push(database.name);
        if (segment.length > 0) nameParts.push(segment);
        if (scheduledDateTime)
            nameParts.push(
                format(scheduledDateTime, "yyyy-MM-dd").replaceAll("-", "") +
                " " +
                format(scheduledDateTime, "HH:mm").replace(":", "h")
            );
        if (volume !== "") nameParts.push(volume.toString());

        return nameParts.join(" - ");
    }, [advertiser, client, database, segment, scheduledDateTime, volume]);


    console.log("\n \t 🕵️ INITIAL VALUES :", initialValues);
    const [message, setMessages] = useState((initialValues && initialValues.cm_message) || STOP_MESSAGE || "");

    // ================================ Affiliate BAT gererate =============================
    // const [affiliateNumber, setAffiliateNumber] = useState((initialValues as any).stats_affiliate_number || "");
    const [affiliateNumber, setAffiliateNumber] = useState<string>(() => {
        return (initialValues as any)?.stats_affiliate_number ?? "";
    });

    const [error, setError] = useState('');
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!/[0-9]/.test(e.key) && e.key !== 'Backspace') {
            e.preventDefault();
            setError("Only numbers are allowed.");
        } else {
            setError('');
        }
    };

    const [comment, setComment] = useState("");

    const [messageWithStop, setMessageWithStop] = useState(STOP_MESSAGE);
    const [maxNumberOfPartsPerRecipient, setMaxNumberOfPartsPerRecipient] = useState(1);
    const msg = useMemo(() => { return maxNumberOfPartsPerRecipient <= 1 ? "message" : "messages"; }, [maxNumberOfPartsPerRecipient]);

    const url = useMemo(() => {
        const match = message?.match(
            /(https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)/g
        );
        return match ? `[[url:{${match[0]}}]]` : "";
    }, [message]);


    // ===================================================== generate url detect in cm message ========================
    const [parsedMessage, setParsedMessage] = React.useState<JSX.Element[]>([]);

    React.useEffect(() => {
        setParsedMessage(parseMessage(message || STOP_MESSAGE));
    }, [message]);

    // Fonction pour détecter les liens et formater le texte
    const parseMessage = (text: string) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const parts = text.split(urlRegex);

        return parts.map((part, index) =>
            urlRegex.test(part) ? (
                <a key={index} href={part} target="_blank" rel="noopener noreferrer" style={{ color: "blue" }}>
                    {part}
                </a>
            ) : (
                <span key={index}>{part}</span>
            )
        );
    };

    // const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     const newValue = event.target.value;
    //     let messageWithoutTags = newValue.replace(/<[^>]*>/g, '');
    //     let messageCleaned = messageWithoutTags.replace(/[^\S\r\n]+/g, ' ');

    //     setMessage(messageCleaned);
    // };

    const [isMaxReached, setIsMaxReached] = useState(false);
    const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        const smsInfo = analyzeSmsCharacters(newValue);

        const maxChars = smsInfo.maxCharactersPerMessage;

        if (smsInfo.messageLength <= maxChars) {
            setMessages(newValue);
            setIsMaxReached(false);
        } else {
            // Trim ce qui dépasse
            let trimmed = newValue.slice(0, -(smsInfo.messageLength - maxChars));
            setMessages(trimmed);

            setIsMaxReached(true);
            setMessage("warning", "The character limit for a single message to this recipient has been reached.");
        }
    };

    // --------------------------------------------------------
    // BLoquer de taper dans un champs de text lorsque la limit est atteint
    // --------------------------------------------------------
    const handleKeyDownEvent = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Si la limite est atteinte
        if (isMaxReached) {
            const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Tab"];
            if (allowedKeys.includes(event.key)) {
                return;
            }
            if (event.ctrlKey && event.key.toLowerCase() === "x") {
                return;
            }
            event.preventDefault();
            setMessage("warning", "The character limit for a single message per recipient has been reached.");
        }
    };


    // const smsDetails = analyzeSmsCharacters(message);
    const smsDetails = useMemo(() => analyzeSmsCharacters(message), [message]); // Correction pour la mémorisation de calcul


    // ============================= Encodage des messages =============================
    const messageToEncode = Array.isArray(message) ? message.join(" ") : String(message);
    const encodedMessage = encode_text_base64(messageToEncode) || "";

    // -------------------------------------------------------------------------
    // Set initial value of advertisier to currently edited campaign's advertiser
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (initialValues) {
            (async () => {
                try {
                    const advertisers = await axiosInstance
                        .get<Advertiser[]>(
                            `${process.env.REACT_APP_API_STATS_GET_ADVERTISER_BY_NAME_ENDPOINT}/${initialValues.stats_advertiser_name}`
                        )
                        .then((r) => r.data);

                    setAdvertisers(advertisers);
                    setAdvertiser(advertisers.length > 0 ? advertisers[0] : null);
                } catch (error) {
                    console.log(
                        `Error while getting advertisers from STATS using keyword '${initialValues.stats_advertiser_name}':`,
                        error
                    );
                }
            })();
        }
    }, [initialValues]);

    // Getting advertiser list requires at least a 3 character keyword
    useEffect(() => {
        if (advertiserInputValue.length < 3) {
            setAdvertisers([]);
        } else {
            (async () => {
                try {
                    const response = await axiosInstance.get<Advertiser[]>(
                        `${process.env.REACT_APP_API_STATS_GET_ADVERTISER_BY_NAME_ENDPOINT}/${advertiserInputValue}`
                    );
                    console.log(`\n \t #======> Advertiser from STATS using keyword '${advertiserInputValue}':`,
                        response.data);
                    setAdvertisers(response.data);
                } catch (error) {
                    console.log(`\n \t #======> Error while getting advertisers from STATS using keyword '${advertiserInputValue}':`, error);
                }
            })();
        }
    }, [advertiserInputValue]);

    // Update currency when country is updated
    useEffect(() => {
        if (country) {
            const currencyInCountry = currencies.find((currency) => currency.id === country.currencyid);
            if (currencyInCountry) setCurrency(currencyInCountry);
        }
    }, [country, currencies]);

    // Show clients with affiliate managers only
    const clientsWithAffiliateManagers = useMemo(
        () => clients.filter((c) => c.affiliatemanagers.length > 0),
        [clients]
    );

    // Filter affiliate manager per client
    const filteredAffiliateManagers = useMemo(
        () => (client ? client.affiliatemanagers : []),
        [client]
    );

    // Clear selected affiliate manager when client changes
    useEffect(() => setAffiliateManager(null), [client]);

    // Set initial value of affiliate manager if provided
    useEffect(() => {
        if (initialValues) {
            setAffiliateManager(
                clients
                    .find((c) => c.id === initialValues.stats_clients_id)?.affiliatemanagers
                    .find((am) => am.id === initialValues.stats_affiliate_id)
                || null
            );
        }
    }, [initialValues, clients]);

    // Cache pricings react node
    const pricingsNode = useMemo(() =>
        pricings.map((pricing, index) => (
            <Grid
                container
                spacing={2}
                item
                xs={12}
                alignItems="center"
                key={index}>
                {/* Select Pricing Model */}
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        value={pricing.model}
                        onChange={(_, newValue) => {
                            setPricings(
                                pricings.map((p, i) => {
                                    if (i === index) return { ...p, model: newValue };
                                    else return p;
                                })
                            );
                        }}
                        disablePortal
                        options={
                            // Ensure pricing model is not already in use
                            models.filter(
                                (model) =>
                                    model === pricing.model ||
                                    !pricings.find((p) => p.model?.id === model.id)
                            )
                        }
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                required
                                {...params}
                                label="Pricing Model"
                                size="small"
                            />
                        )}
                        renderOption={(props, option) => (
                            <li {...props} key={option.id}>
                                {option.name}
                            </li>
                        )}
                    />
                </Grid>

                {/* Select Price */}
                <Grid item xs={12} md={3}>
                    <TextField
                        required
                        value={pricing.price || ""}
                        onChange={(event) => {
                            // Ensure value is a number
                            if (!event.target.value.match(/^[0-9]*\.?[0-9]*$/)) return;

                            setPricings(
                                pricings.map((p, i) =>
                                    i === index ? { ...p, price: event.target.value || "", } : p
                                )
                            );
                        }}
                        fullWidth
                        label="Price"
                        size="small"
                    />
                </Grid>

                {/* Add/Delete pricing */}
                <Grid item xs={12} md={2} justifyContent="center">
                    {/* Can remove pricing only if there is at least 2 */}
                    {pricings.length > 1 && (
                        <IconButton
                            onClick={() => { setPricings(pricings.filter((_, i) => i !== index)); }}
                            color="error">
                            <RemoveCircleIcon />
                        </IconButton>
                    )}

                    {/* Can only have as much pricing as the number of pricing models */}
                    {pricings.length < models.length &&
                        index === pricings.length - 1 && (
                            <IconButton
                                onClick={() => { setPricings([...pricings, { model: null, price: "" }]); }}
                                color="primary">
                                <AddCircleIcon />
                            </IconButton>
                        )}
                </Grid>

                <input type="text" name="payouts_info" value={JSON.stringify(pricings.map((p) => ({ modelId: p.model?.id, payout: p.price, })))} readOnly hidden />
            </Grid>
        )),
        [pricings, models]
    );

    return (
        <Box component={fetcher.Form} method="patch" sx={{ mt: 0, p: 0, backgroundColor: "#f9f9f9", borderRadius: 2 }}>
            {/* Set campaign id if this is an edit form */}
            {initialValues && (
                <>
                    <input type="hidden" name="campaign-id" value={initialValues.id} />
                    <input type="hidden" name="cm_id" value={initialValues?.cm_id} />
                </>
            )}
            {fetcher.state === "idle" && fetcher.data && fetcher.data.error && (
                <Grid item xs={12} spacing={2}>
                    <Alert severity="error">{fetcher.data.error}</Alert>
                    <br />
                </Grid>
            )}

            <Grid container spacing={2}>
                {/* ====================================== Select Database ========================================*/}
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        value={database}
                        onChange={(_, newValue) => { setDatabase(newValue); }}
                        disablePortal
                        options={filteredDatabases}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (<TextField required {...params} label="Database" />)}
                        renderOption={(props, option) => (<li {...props} key={option.id}> {option.name} </li>)}
                        size="small"
                    />
                    <input type="number" name="stats_base_id" value={database?.id} hidden readOnly />
                    <input type="text" name="stats_base_name" value={database?.name} hidden readOnly />
                </Grid>

                {/* ================================================= Filter databases by kind =====================================*/}
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        value={databaseKind}
                        onChange={(_, newValue) => { setDatabaseKind(newValue || defaultDatabaseKind); }}
                        disablePortal
                        options={databaseKinds}
                        getOptionLabel={(option) => option.charAt(0).toUpperCase() + option.slice(1)}
                        renderInput={(params) => (<TextField {...params} label="Database Kind" />)}
                        renderOption={(props, option) => (<li {...props} key={option}> {option.charAt(0).toUpperCase() + option.slice(1)} </li>)}
                        size="small"
                    />
                </Grid>

                {/* ================================================ Select Country ================================================*/}
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        value={country}
                        onChange={(_, newValue) => { setCountry(newValue); }}
                        disablePortal
                        options={countries}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => `${option.countrycode} - ${option.name}`}
                        renderInput={(params) => (<TextField required {...params} label="Country" />)}
                        renderOption={(props, option) => (<li {...props} key={option.id}> {option.countrycode} - {option.name} </li>)}
                        size="small"
                    />
                    <input type="number" name="stats_country_id" value={country?.id} readOnly hidden />
                    <input type="text" name="stats_country_name" value={country?.countrycode} readOnly hidden />
                </Grid>

                {/* =========================================================== Select Currency ================================================*/}
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        value={currency}
                        onChange={(_, newValue) => { setCurrency(newValue); }}
                        disablePortal
                        options={currencies}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (<TextField required {...params} label="Currency" />)}
                        renderOption={(props, option) => (<li {...props} key={option.id}> {option.name} </li>)}
                        size="small"
                    />
                    <input type="number" name="stats_currency_id" value={currency?.id} readOnly hidden />
                    <input type="text" name="stats_currency_name" value={currency?.name} readOnly hidden />
                </Grid>

                {/* ========================================= Select Advertiser ========================================= */}
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        value={advertiser}
                        onChange={(_, newValue) => { setAdvertiser(newValue); }}
                        inputValue={advertiserInputValue}
                        onInputChange={(_, newInputValue) => { setAdvertiserInputValue(newInputValue); }}
                        disablePortal
                        options={advertisers}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (
                            <TextField required {...params} label="Advertiser (enter the first 3 characters of the advertiser)" />
                        )}
                        renderOption={(props, option) => (<li {...props} key={option.id}> {option.name} </li>)}
                        size="small"
                    />
                    <input type="number" name="stats_advertiser_id" value={advertiser?.id} readOnly hidden />
                    <input type="text" name="stats_advertiser_name" value={advertiser?.name} readOnly hidden />
                </Grid>

                {/* ================================================ Select Tag ========================================== */}
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        value={tag}
                        onChange={(_, newValue) => { setTag(newValue); }}
                        disablePortal
                        options={tags}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.tag}
                        renderInput={(params) => (<TextField required {...params} label="Tag" />)}
                        renderOption={(props, option) => (<li {...props} key={option.id}> {option.tag} </li>)}
                        size="small"
                    />
                    <input type="number" name="stats_tags_id" value={tag?.id} readOnly hidden />
                    <input type="text" name="stats_tags_name" value={tag?.tag} readOnly hidden />
                    <input type="text" name="stats_dwtag_name" value={tag?.dwtag} readOnly hidden />
                </Grid>

                {/* ===================================================== Select Client ================================================*/}
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        value={client}
                        onChange={(_, newValue) => { setClient(newValue); }}
                        disablePortal
                        options={clientsWithAffiliateManagers}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (<TextField required {...params} label="Client" />)}
                        renderOption={(props, option) => (<li {...props} key={option.id}> {option.name} </li>)}
                        size="small"
                    />
                    <input type="number" name="stats_clients_id" value={client?.id} readOnly hidden />
                    <input type="text" name="stats_clients_name" value={client?.name} readOnly hidden />
                </Grid>

                {/* ============================================ Select Affiliate Manager =====================================================*/}
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        value={affiliateManager}
                        onChange={(_, newValue) => { setAffiliateManager(newValue); }}
                        disablePortal
                        options={filteredAffiliateManagers}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (<TextField required {...params} label="Affiliate Manager" />)}
                        renderOption={(props, option) => (<li {...props} key={option.id}> {option.name} </li>)}
                        size="small"
                    />
                    <input type="number" name="stats_affiliate_id" value={affiliateManager?.id} readOnly hidden />
                    <input type="text" name="stats_affiliate_name" value={affiliateManager?.name} readOnly hidden />
                </Grid>

                {/* ===============================================Select pricing models along with prices =============================*/}
                {pricingsNode}

                {/* ========================================= Stats segment input ======================================*/}
                <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Segment" variant="outlined" name="stats_segment" value={segment} onChange={(event) => setSegment(event.target.value)} required size="small" />
                </Grid>

                {/* ================================================== Select stats status =============================================*/}
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        value={statsStatus}
                        onChange={(_, newValue) => { setStatsStatus(newValue); }}
                        disablePortal
                        options={STATS_STATUSES}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.value}
                        renderInput={(params) => (<TextField required {...params} label="Status" />)}
                        renderOption={(props, option) => (<li {...props} key={option.id}> {option.value} </li>)}
                        size="small"
                    />
                    <input type="number" name="stats_status" value={statsStatus?.id} hidden readOnly />
                </Grid>

                {/* ====================================================== Select volume ===============================================*/}
                <Grid item xs={12} md={6}>
                    <TextField fullWidth type="number" label="Volume" name="volume" value={volume} onChange={(event) => setVolume(parseInt(event.target.value) || "")} required size="small" />
                </Grid>

                {/* ============================================== Enter stats scheduled date and time ===================================*/}
                <Grid item xs={12} md={6}>
                    <DateTimePicker
                        label="Scheduled Date & Time"
                        value={scheduledDateTime}
                        inputFormat="dd/MM/yyyy HH:mm"
                        onChange={(newValue) => { setScheduledDateTime(newValue); }}
                        renderInput={(params) => (<TextField fullWidth required {...params} size="small" />)}
                        ampm={false}
                        disablePast={true}
                    />
                    <input type="text" name="stats_scheduled_date" value={scheduledDateTime?.toISOString()} readOnly hidden />
                    <input type="text" name="stats_scheduled_time" value={scheduledDateTime ? format(scheduledDateTime, "HH:mm").replace(":", "h") : ""} readOnly hidden />
                </Grid>


                {/* ========================================= Sender input ========================================*/}
                <Grid item xs={12} md={6}>
                    <TextField
                        value={sender}
                        onChange={(event) => {
                            const newValue = event.target.value;
                            const containsInvalidCharacters = /[^a-zA-Z]/.test(newValue);
                            if (containsInvalidCharacters) { return; }
                            if (newValue.length > MAX_SENDER_TEXT_LENGTH) { return; }
                            setSender(newValue);
                        }}
                        name="cm_sender"
                        required
                        label="Sender"
                        helperText={`${sender.length}/${MAX_SENDER_TEXT_LENGTH} characters`}
                        fullWidth
                        variant="outlined"
                        size="small"
                    />
                </Grid>

                {/* ====================================== Select Address Book (Recipients)  ======================================*/}
                <Grid item xs={12} md={6}>
                    <Autocomplete
                        value={addressBook}
                        onChange={(_, newValue) => { setAddressBook(newValue); }}
                        disablePortal
                        options={addressBooksWithDefault}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (<TextField required {...params} label="Recipients" size="small" />)}
                        renderOption={(props, option) => (<li {...props} key={option.id}> {option.name} </li>)}
                    />
                    <input type="text" name="cm_group_id" value={addressBook?.id} hidden readOnly />
                </Grid>

                {/* ======================================= ADD COMMENTS AND AFFILIATE BAT HERE ======================================*/}
                {/* comments input */}
                <Grid item xs={12} md={6}>
                    {/* <TextField
                        value={comment}
                        onChange={(event) => {
                            const newValue = event.target.value;
                            return setComment(newValue);
                        }}
                        name="comment"
                        label="Comments"
                        fullWidth
                        variant="outlined"
                        size="small"
                    /> */}
                    <TextField
                        value={comment}
                        onChange={(event) => {
                            const newValue = event.target.value;
                            setComment(newValue ?? "");
                        }}
                        name="comment"
                        label="Comments"
                        fullWidth
                        variant="outlined"
                        size="small"
                    />

                </Grid>


                {/* ============================================== Affiliate BAT ============================================*/}
                <Grid item xs={12} md={6}>
                    {/* <TextField
                        value={affiliateNumber}
                        onChange={(event) => {
                            const newValue = event.target.value;
                            if (newValue.length > 12) return;
                            return setAffiliateNumber(newValue);
                        }}
                        name="stats_affiliate_number"
                        label="Affiliate BAT"
                        fullWidth
                        variant="outlined"
                        size="small"
                    /> */}
                    <TextField
                        value={affiliateNumber}
                        onChange={(event) => {
                            const newValue = event.target.value;
                            if (newValue === "" || newValue.length <= 12) {
                                setAffiliateNumber(newValue);
                            }

                            // Expression régulière : chiffres uniquement
                            if (/^\d*$/.test(newValue)) {
                                setAffiliateNumber(newValue);
                                setError("");
                            } else {
                                setError("Only numbers are allowed.");
                            }
                        }}
                        name="stats_affiliate_number"
                        label="Affiliate BAT"
                        fullWidth
                        variant="outlined"
                        size="small"

                        onKeyDown={handleKeyDown}

                    />
                    {error && <p style={{ color: 'red' }}>{error}</p>}

                </Grid>
                {/* ADD COMMENTS AND AFFILIATE BAT HERE */}

                {/* Campaign name input */}
                <Grid item xs={12}>
                    <TextField fullWidth label="Campaign name" variant="outlined" name="name" value={campaignName} InputLabelProps={{ shrink: true, }} InputProps={{ readOnly: true, }} required size="small" />
                    <input type="text" name="cm_name" value={campaignName} hidden readOnly />
                </Grid>

                {/* ======================================== Campaign message input ======================================== */}
                <Grid container spacing={2} item xs={12} alignItems="stretch">
                    <Grid item xs={12} md={6}>
                        <TextField
                            value={message}
                            onChange={handleMessageChange}
                            name="cm_message"
                            required
                            onKeyDown={handleKeyDownEvent}
                            label="Message"
                            // Routing cost about : £${smsDetails.numberOfMessages * 0.0332} per recipient,
                            helperText={`
                                ${smsDetails.numberOfMessages} message(s) per recipient,
                                ${smsDetails.maxCharactersPerMessage - (smsDetails.messageLength)} characters left, 
                                
                                unsupported characters : ${Object.entries(smsDetails.unsupportedCharacters).length > 0
                                    ? Object.entries(smsDetails.unsupportedCharacters)
                                        .map(([char, count]) => `caractère : "${char}" nombre de recurence : ${count}`)
                                        .join(", ")
                                    : "none"
                                }
                            `}
                            multiline
                            fullWidth
                            size="small"
                            rows={5}
                        />
                        {/* Champ caché pour inclure STOP_MESSAGE */}
                        {/* <LinkOutlined /> */}
                        {/* <input type="hidden" name="cm_message" value={messageWithStop} /> */}
                        <input type="text" name="cm_url" value={smsDetails.detectedLinks} hidden readOnly />
                        <input type="hidden" name="cm_message" value={encodedMessage} />
                    </Grid>


                    <Grid item xs={12} md={6} alignItems="stretch">
                        <Paper
                            sx={{
                                width: "100%",
                                minHeight: theme.spacing(5),
                                paddingY: 0.0625,
                                paddingX: 1.75,
                                whiteSpace: "pre-wrap",
                                backgroundColor: "success.main",
                                color: "text.success",
                                position: "relative"
                            }}>
                            <Typography color="black" variant="body1" style={{ maxHeight: "130px", fontWeight: "900", height: "130px", overflowY: "auto" }}>
                                {parsedMessage}
                                {/* <p style={{ position: "absolute", bottom: 0, left: 0, margin: 0, fontWeight: "bold", backgroundColor: "#D6DBDF", padding: "5px" }}> {STOP_MESSAGE} </p> */}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Grid>

            {/* ================================== action message generating =========================== */}
            <ToastContainer />
            {/* Submit form */}
            <LoadingButton
                type="submit"
                loading={fetcher.state === "submitting"}
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={inputDisabled}>
                <Box sx={{ height: 28, display: "flex", justifyContent: "center", alignItems: "center" }}>
                    {inputDisabled ? (<CircularProgress size={16} />) : action === CampaignFormActions.CREATE ? ("Create") : ("Save")}
                </Box>
            </LoadingButton>
        </Box >
    );
};

export default CampaignForm;